import { ChatbotContext } from './chatbotContext';
import { BOT_INTENTS } from '../constants/intents';

export interface RouteSuggestion {
  label: string;
  query: string;
  intent: string | null;
}

export function getRouteSuggestions(pathname: string, search: string, context: ChatbotContext): RouteSuggestion[] {
  const searchParams = new URLSearchParams(search);
  const tab = searchParams.get('tab');

  // 1. Change Password /profile?tab=change_password
  if (pathname === '/profile' && tab === 'change_password') {
    return [
      { label: 'Change Password', query: 'Change Password', intent: BOT_INTENTS.UPDATE_SHIPMENT_STATUS || null },
      { label: 'Update Password', query: 'Update Password', intent: null },
      { label: 'Password Requirements', query: 'Password Requirements', intent: null },
    ];
  }

  // 2. Profile /profile
  if (pathname === '/profile') {
    return [
      { label: 'Update Profile', query: 'Update Profile', intent: null },
      { label: 'Change Password', query: 'Change Password', intent: null },
      { label: 'Manage Account', query: 'Manage Account', intent: null },
    ];
  }

  // 3. Dashboard / (home)
  if (pathname === '/') {
    return [
      { label: 'Track Shipment', query: 'Track Shipment', intent: BOT_INTENTS.TRACK_SHIPMENT },
      { label: 'View Shipment Reports', query: 'View Shipment Reports', intent: BOT_INTENTS.DOWNLOAD_REPORT },
    ];
  }

  // 4. Get Rates /get-rates
  if (pathname === '/get-rates') {
    return [
      { label: 'Find Rates', query: 'Find Rates', intent: BOT_INTENTS.GET_RATES },
      { label: 'Get Exact Rates', query: 'Get Exact Rates', intent: BOT_INTENTS.GET_RATES },
      { label: 'Compare Carrier Rates', query: 'Compare Carrier Rates', intent: BOT_INTENTS.GET_RATES },
      { label: 'Check Transit Time', query: 'Check Transit Time', intent: null },
    ];
  }

  // 5. User /user
  if (pathname === '/user') {
    return [
      { label: 'Get Outstanding Balance', query: 'Get Outstanding Balance', intent: null },
      { label: 'Deactivate/Activate User', query: 'Deactivate/Activate User', intent: null },
      { label: 'Convert Prepaid ↔ Postpaid', query: 'Convert Prepaid ↔ Postpaid', intent: null },
    ];
  }

  // 6. Admin /admin
  if (pathname === '/admin') {
    return [
      { label: 'Check KYC Status', query: 'Check KYC Status', intent: null },
      { label: 'Complete KYC', query: 'Complete KYC', intent: null },
      { label: 'Manage KYC', query: 'Manage KYC', intent: null },
    ];
  }

  // 7. Shipment History /shipment/history
  if (pathname === '/shipment/history') {
    return [
      { label: 'Download Shipment Report', query: 'Download Shipment Report', intent: BOT_INTENTS.DOWNLOAD_REPORT },
      { label: 'Repeat Shipment', query: 'Repeat Shipment', intent: BOT_INTENTS.REPEAT_SHIPMENT },
      { label: 'Download Label', query: 'Download Label', intent: BOT_INTENTS.DOWNLOAD_AWB },
      { label: 'Track Shipment', query: 'Track Shipment', intent: BOT_INTENTS.TRACK_SHIPMENT },
      { label: 'Shipment Status', query: 'Shipment Status', intent: BOT_INTENTS.SHIPMENT_STATUS },
    ];
  }

  // 8. Shipment Details /shipment/history/:shipmentNumber and /shipment/admin/edit/:shipmentNumber
  const isShipmentDetails =
    pathname.match(/^\/shipment\/history\/([^/]+)$/) ||
    pathname.match(/^\/shipment\/admin\/edit\/([^/]+)$/) ||
    pathname.match(/^\/shipment\/admin\/unprocessed\/edit\/([^/]+)$/);

  if (isShipmentDetails && context.shipmentNumber) {
    const sNum = context.shipmentNumber;
    return [
      { label: 'Repeat Shipment', query: `Repeat Shipment ${sNum}`, intent: BOT_INTENTS.REPEAT_SHIPMENT },
      { label: 'Update Shipment', query: `Update Shipment ${sNum}`, intent: BOT_INTENTS.UPDATE_SHIPMENT },
      { label: 'Download Carrier AWB', query: `Download Carrier AWB for ${sNum}`, intent: BOT_INTENTS.DOWNLOAD_AWB },
      { label: 'Shipment Status', query: `Shipment Status for ${sNum}`, intent: BOT_INTENTS.SHIPMENT_STATUS },
      { label: 'Add Charges', query: `Add Charges to ${sNum}`, intent: null },
      {
        label: 'Update Shipment Status',
        query: `Update Shipment Status for ${sNum}`,
        intent: BOT_INTENTS.UPDATE_SHIPMENT_STATUS,
      },
      { label: 'Cancel Shipment', query: `Cancel Shipment ${sNum}`, intent: BOT_INTENTS.CANCEL_SHIPMENT },
    ];
  }

  // 9. Create Single Shipment /shipment/create-single or /shipment
  if (pathname === '/shipment/create-single' || pathname === '/shipment' || pathname === '/shipment/create') {
    return [
      { label: 'Add Pickup Address', query: 'Add Pickup Address', intent: null },
      { label: 'Add Delivery Address', query: 'Add Delivery Address', intent: null },
      { label: 'Add Package Details', query: 'Add Package Details', intent: null },
      { label: 'Add Weight', query: 'Add Weight', intent: null },
      { label: 'Get Shipping Rates', query: 'Get Shipping Rates', intent: BOT_INTENTS.GET_RATES },
    ];
  }

  // 10. Invoice /invoice/view
  if (pathname === '/invoice/view') {
    return [
      { label: 'Create Invoice', query: 'Create Invoice', intent: BOT_INTENTS.CREATE_INVOICE },
      { label: 'Download Invoice', query: 'Download Invoice', intent: null },
      { label: 'Download Report', query: 'Download Report', intent: BOT_INTENTS.DOWNLOAD_REPORT },
    ];
  }

  // 11. Transactions /transactions/list
  if (pathname === '/transactions/list' || pathname === '/transactions') {
    return [
      {
        label: 'Download Transaction Report',
        query: 'Download Transaction Report',
        intent: BOT_INTENTS.DOWNLOAD_REPORT,
      },
      { label: 'View Transactions', query: 'View Transactions', intent: null },
      { label: 'Check Transaction Details', query: 'Check Transaction Details', intent: null },
    ];
  }

  // 12. Credits & Wallets /transactions/credits-and-wallets
  if (pathname === '/transactions/credits-and-wallets') {
    return [
      { label: 'Download Report', query: 'Download Report', intent: BOT_INTENTS.DOWNLOAD_REPORT },
      { label: 'Check Wallet Balance', query: 'Check Wallet Balance', intent: null },
      { label: 'View Credit Transactions', query: 'View Credit Transactions', intent: null },
    ];
  }

  // 13. Purchase Invoices /purchase-invoices
  if (pathname === '/purchase-invoices' || pathname === '/purchase-invoices/list') {
    return [
      { label: 'Create Purchase Invoice', query: 'Create Purchase Invoice', intent: BOT_INTENTS.CREATE_INVOICE },
      { label: 'Download Report', query: 'Download Report', intent: BOT_INTENTS.DOWNLOAD_REPORT },
      { label: 'View Purchase Invoices', query: 'View Purchase Invoices', intent: null },
    ];
  }

  // 14. Purchase Invoice Credits / Transactions /purchase-invoices/credits-transaction
  if (pathname === '/purchase-invoices/credits-transaction') {
    return [
      { label: 'Download Report', query: 'Download Report', intent: BOT_INTENTS.DOWNLOAD_REPORT },
      { label: 'View Credit Transactions', query: 'View Credit Transactions', intent: null },
      { label: 'Check Purchase Invoice Credits', query: 'Check Purchase Invoice Credits', intent: null },
    ];
  }

  return [];
}
